import React, { useMemo } from "react";
import {
  ResponsiveContainer, ComposedChart, AreaChart, Area, BarChart, Bar,
  Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine,
  LabelList,
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// FinancialOverview — bespoke editorial layout for the Finance dashboard's
// Overview tab. Replaces the generic KPI-strip + 4-chart grid with:
//
//   1. Context strip (FY · period · scope) in small-caps editorial style
//   2. Hero KPI band — 3 large tiles:
//        · YTD Actuals (huge number + 12-period sparkline)
//        · YTD Budget  (huge number + composition hint)
//        · Budget Utilised (circular progress meter + status pill)
//   3. Secondary KPI chips — FY Actuals · FY Budget · YTD Revenue
//   4. Primary chart — Monthly Actuals vs Budget, composed area+line, larger
//   5. Spend by directorate (drillable, refined bars with value labels)
//   6. Top expense accounts (refined)
//
// Stays inside the immutable Boroondara brand — Segoe UI, brand green accent,
// light theme. Visual hierarchy is the differentiation, not chrome.
// ─────────────────────────────────────────────────────────────────────────────

interface KpiEntry { label: string; raw: number; suffix?: string; detail?: string; hint?: string }
interface Charts {
  monthlyExpVsBudget?: Array<{ Month: string; PeriodNum: number; Actuals: number; Budget: number }>;
  rollupSpend?: Array<{ Name: string; Spend: number }>;
  rollupLevel?: string;
  spendByAccount?: Array<{ Account: string; Spend: number }>;
}

interface Props {
  kpis: Record<string, KpiEntry>;
  charts: Charts;
  tabColor: string;
  tabPalette: string[];
  fy: string;
  period: string;
  periodLabel: string;
  excludeProject: boolean;
  onDrillBar?: (entry: any) => void;
}

const GREEN = "#2e873d";
const GREEN_DEEP = "#226a2e";
const TEXT_1 = "#232323";
const TEXT_2 = "#484848";
const TEXT_3 = "#747474";
const TEXT_4 = "#9aa0a6";
const BORDER = "#E1E1E1";
const BORDER_SOFT = "#EDEDED";

// ── Currency formatting (consistent thresholds) ──
function fmtBig(n: number): { value: string; unit: string } {
  const v = Math.abs(n);
  if (v >= 1_000_000) return { value: (n / 1_000_000).toFixed(2), unit: "M" };
  if (v >= 1_000)     return { value: (n / 1_000).toFixed(0), unit: "k" };
  return { value: String(Math.round(n)), unit: "" };
}
function fmtAxis(v: number): string {
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (Math.abs(v) >= 1_000)     return (v / 1_000).toFixed(0) + "k";
  return String(Math.round(v));
}
function fmtCurrencyFull(v: any): string {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return "$" + Math.round(n).toLocaleString("en-AU");
}
function truncate(s: string, max = 32): string {
  return s && s.length > max ? s.slice(0, max) + "…" : (s || "");
}

// ── Utilisation status thresholds ──
// Absolute (not period-aware). Adjust if you want a period-progress-aware
// version (e.g. "at period 8 of 12 we'd expect ~67%").
function statusFor(pct: number): { label: string; tone: "ok" | "watch" | "limit" | "over" } {
  if (pct >= 100) return { label: "OVER BUDGET",      tone: "over"  };
  if (pct >= 90)  return { label: "AT LIMIT",         tone: "limit" };
  if (pct >= 60)  return { label: "APPROACHING",      tone: "watch" };
  return            { label: "ON TRACK",            tone: "ok"    };
}
const STATUS_COLORS: Record<string, string> = {
  ok:    GREEN,
  watch: "#D97706",
  limit: "#DC2626",
  over:  "#991B1B",
};

// ── Circular progress meter (SVG) ──
function CircularProgress({ pct, color }: { pct: number; color: string }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const size = 132;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Budget utilised ${clamped.toFixed(1)}%`}>
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="#EDEDED" strokeWidth={stroke}
      />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dasharray 0.5s cubic-bezier(.4,1.2,.4,1)" }}
      />
      <text
        x="50%" y="48%"
        textAnchor="middle" dominantBaseline="central"
        style={{
          fontSize: 26, fontWeight: 800, fill: TEXT_1,
          fontFamily: "Segoe UI, system-ui, sans-serif",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
        }}
      >
        {clamped.toFixed(0)}%
      </text>
      <text
        x="50%" y="68%"
        textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: 9.5, fontWeight: 700, fill: TEXT_3, letterSpacing: "0.1em", fontFamily: "Segoe UI, sans-serif" }}
      >
        UTILISED
      </text>
    </svg>
  );
}

// ── Mini sparkline (12-period actuals) ──
function HeroSparkline({ data, color }: { data: any[]; color: string }) {
  if (!data || data.length === 0) return null;
  return (
    <div style={{ height: 38, width: 140, marginLeft: "auto" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="hero-spark-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.32} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="Actuals"
            stroke={color}
            strokeWidth={1.5}
            fill="url(#hero-spark-fill)"
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Big-number rendering ──
function HeroValue({ raw, isPercent = false }: { raw: number; isPercent?: boolean }) {
  if (isPercent) {
    return (
      <div style={{
        fontSize: 44, fontWeight: 800, color: TEXT_1, letterSpacing: "-0.025em",
        fontVariantNumeric: "tabular-nums", lineHeight: 1,
      }}>
        {(Number(raw) || 0).toFixed(1)}<span style={{ fontSize: 26, color: TEXT_3 }}>%</span>
      </div>
    );
  }
  const { value, unit } = fmtBig(Number(raw) || 0);
  return (
    <div style={{
      fontSize: 44, fontWeight: 800, color: TEXT_1, letterSpacing: "-0.025em",
      fontVariantNumeric: "tabular-nums", lineHeight: 1,
    }}>
      <span style={{ fontSize: 22, fontWeight: 600, color: TEXT_3, marginRight: 2, verticalAlign: "top", letterSpacing: 0 }}>$</span>
      {value}
      {unit && <span style={{ fontSize: 26, color: TEXT_3, marginLeft: 2 }}>{unit}</span>}
    </div>
  );
}

// ── Custom value label for the rollup bars ──
function rollupValueLabel({ x, y, width, height, value }: any) {
  if (!isFinite(value) || value === 0) return null;
  return (
    <text
      x={x + width + 8}
      y={y + height / 2}
      dominantBaseline="central"
      style={{
        fontSize: 11.5,
        fontFamily: "Segoe UI, system-ui, sans-serif",
        fontVariantNumeric: "tabular-nums",
        fill: TEXT_2,
        fontWeight: 600,
      }}
    >
      {fmtAxis(value)}
    </text>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FinancialOverview({
  kpis, charts, tabColor, tabPalette,
  fy, period, periodLabel, excludeProject, onDrillBar,
}: Props) {
  // KPI values, defaulting to 0 if missing.
  const ytdActuals = Number(kpis.ytdActuals?.raw ?? 0);
  const fyActuals  = Number(kpis.fyActuals?.raw  ?? 0);
  const ytdBudget  = Number(kpis.ytdBudget?.raw  ?? 0);
  const fyBudget   = Number(kpis.fyBudget?.raw   ?? 0);
  const utilisation = Number(kpis.budgetUtilisation?.raw ?? 0);
  const ytdRevenue = Number(kpis.ytdRevenue?.raw ?? 0);

  const status = statusFor(utilisation);
  const statusColor = STATUS_COLORS[status.tone];

  // Sparkline data: just the actuals series across the available periods
  const sparkData = useMemo(
    () => (charts.monthlyExpVsBudget || []).map((p) => ({ Month: p.Month, Actuals: Math.abs(Number(p.Actuals) || 0) })),
    [charts.monthlyExpVsBudget]
  );

  // For the primary chart we want the current-period marker line. If period
  // isn't selected, no marker.
  const currentPeriodNum = period ? parseInt(period, 10) : null;
  const currentPeriodLabel = (charts.monthlyExpVsBudget || []).find((p) => p.PeriodNum === currentPeriodNum)?.Month;

  // Compute period progress for context display (1..12). If no period selected,
  // show "Year to date" without a specific period.
  const periodCtx = period && periodLabel
    ? `Period ${period} · ${periodLabel}`
    : period
    ? `Period ${period}`
    : "Year to date";

  return (
    <>
      <style>{`
        /* ── Context eyebrow strip ── */
        .fo-context {
          display: flex; align-items: center; gap: 14px;
          padding: 14px 18px;
          background: linear-gradient(to right, ${tabColor}0d, transparent 80%);
          border: 1px solid #E4E4E4;
          border-left: 3px solid ${tabColor};
          border-radius: 6px;
          margin-bottom: 16px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: ${TEXT_2};
          font-variant-numeric: tabular-nums;
        }
        .fo-context .fo-ctx-sep {
          color: ${TEXT_4}; font-weight: 400; letter-spacing: 0;
        }
        .fo-context .fo-ctx-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 3px 9px;
          background: #FFFFFF;
          border: 1px solid ${BORDER};
          border-radius: 12px;
          color: ${TEXT_2};
          letter-spacing: 0.06em;
        }
        .fo-context .fo-ctx-strong {
          color: ${tabColor};
          letter-spacing: 0.04em;
        }

        /* ── Hero band ── */
        .fo-hero {
          display: grid;
          grid-template-columns: 1.25fr 1fr 1fr;
          gap: 14px;
          margin-bottom: 14px;
        }
        @media (max-width: 1100px) {
          .fo-hero { grid-template-columns: 1fr 1fr; }
          .fo-hero-utilisation { grid-column: span 2; }
        }
        @media (max-width: 680px) {
          .fo-hero { grid-template-columns: 1fr; }
          .fo-hero-utilisation { grid-column: auto; }
        }
        .fo-hero-tile {
          background: #FFFFFF;
          border: 1px solid #E4E4E4;
          border-top: 3px solid ${tabColor};
          border-radius: 8px;
          padding: 22px 24px 20px;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
          position: relative;
          overflow: hidden;
        }
        .fo-hero-head {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }
        .fo-eyebrow {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: ${TEXT_3};
        }
        .fo-eyebrow .fo-eyebrow-hint {
          color: ${TEXT_4}; font-weight: 500; letter-spacing: 0.04em;
        }
        .fo-hero-sub {
          margin-top: 12px;
          font-size: 12px;
          color: ${TEXT_3};
          line-height: 1.4;
        }
        .fo-hero-sub strong { color: ${TEXT_2}; font-weight: 600; }

        .fo-hero-utilisation {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 10px;
          padding: 16px 20px 18px;
        }
        .fo-status-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 12px;
          border-radius: 14px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .fo-status-dot {
          width: 6px; height: 6px; border-radius: 50%;
        }
        .fo-util-context {
          font-size: 11.5px;
          color: ${TEXT_3};
          text-align: center;
          line-height: 1.4;
        }
        .fo-util-context strong { color: ${TEXT_2}; font-weight: 600; }

        /* Mini bullet bar shown under YTD Budget tile */
        .fo-bullet {
          margin-top: 12px;
          height: 6px;
          background: #F2F2F2;
          border-radius: 3px;
          overflow: hidden;
          position: relative;
        }
        .fo-bullet-fill {
          height: 100%;
          background: linear-gradient(to right, ${tabColor}, ${GREEN_DEEP});
          border-radius: 3px;
          transition: width 0.5s cubic-bezier(.4,1.2,.4,1);
        }
        .fo-bullet-marker {
          position: absolute; top: -2px; bottom: -2px;
          width: 2px;
          background: ${TEXT_2};
          opacity: 0.6;
        }

        /* ── Secondary KPI chip row ── */
        .fo-secondary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 18px;
        }
        @media (max-width: 700px) {
          .fo-secondary { grid-template-columns: 1fr; }
        }
        .fo-secondary-chip {
          background: #FFFFFF;
          border: 1px solid #E4E4E4;
          border-left: 4px solid ${tabColor};
          border-radius: 6px;
          padding: 12px 16px;
          display: flex; align-items: center; gap: 14px;
        }
        .fo-secondary-chip .fo-chip-label {
          font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
          color: ${TEXT_3}; flex-shrink: 0;
        }
        .fo-secondary-chip .fo-chip-value {
          font-size: 17px; font-weight: 700; color: ${TEXT_1};
          font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
          margin-left: auto;
        }

        /* ── Section header (small caps with rule) ── */
        .fo-section-head {
          display: flex; align-items: center; gap: 12px;
          margin: 28px 0 10px;
        }
        .fo-section-head .fo-section-title {
          font-size: 11px; font-weight: 700; letter-spacing: 0.16em;
          text-transform: uppercase; color: ${TEXT_2};
        }
        .fo-section-head .fo-section-rule {
          flex: 1; height: 1px; background: #E4E4E4;
        }
        .fo-section-head .fo-section-hint {
          font-size: 11px; color: ${TEXT_3}; font-weight: 500; letter-spacing: 0.04em;
        }

        /* ── Chart cards (refined) ── */
        .fo-chart-card {
          background: #FFFFFF;
          border: 1px solid #E4E4E4;
          border-top: 3px solid ${tabColor};
          border-radius: 8px;
          padding: 20px 22px 22px;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
          margin-bottom: 14px;
        }
        .fo-chart-card-header {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 12px;
        }
        .fo-chart-card-title {
          font-size: 14px; font-weight: 700; color: ${TEXT_1};
          letter-spacing: -0.005em;
        }
        .fo-chart-card-meta {
          display: flex; gap: 14px; align-items: center;
          font-size: 11px; color: ${TEXT_3};
        }
        .fo-legend-swatch {
          display: inline-flex; align-items: center; gap: 6px;
        }
        .fo-legend-swatch i {
          width: 10px; height: 10px; border-radius: 2px;
          display: inline-block;
        }
        .fo-drill-hint {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 3px 9px;
          background: ${tabColor}10;
          color: ${tabColor};
          border-radius: 10px;
          font-size: 10.5px; font-weight: 600; letter-spacing: 0.04em;
          text-transform: uppercase;
        }
      `}</style>

      {/* ── Context strip ── */}
      <div className="fo-context">
        <span className="fo-ctx-strong">FY {fy || "—"}</span>
        <span className="fo-ctx-sep">·</span>
        <span>{periodCtx}</span>
        <span className="fo-ctx-sep">·</span>
        <span className="fo-ctx-pill">
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: tabColor, display: "inline-block" }} />
          {excludeProject ? "Operating only" : "All postings"}
        </span>
        <span style={{ marginLeft: "auto", color: TEXT_4, fontWeight: 500, letterSpacing: 0.04 }}>
          GL expense (acct 5*) + project (attributed)
        </span>
      </div>

      {/* ── Hero band ── */}
      <div className="fo-hero">
        {/* YTD Actuals — primary */}
        <div className="fo-hero-tile fo-hero-tile-primary">
          <div className="fo-hero-head">
            <div>
              <div className="fo-eyebrow">YTD Actuals</div>
            </div>
            <HeroSparkline data={sparkData} color={tabColor} />
          </div>
          <HeroValue raw={Math.abs(ytdActuals)} />
          <div className="fo-hero-sub">
            Through {periodCtx} · FY {fy || "—"}
            {fyActuals !== ytdActuals && (
              <> · <strong>{Math.round((Math.abs(ytdActuals) / Math.abs(fyActuals || 1)) * 100)}%</strong> of FY total</>
            )}
          </div>
        </div>

        {/* YTD Budget — secondary */}
        <div className="fo-hero-tile">
          <div className="fo-hero-head">
            <div className="fo-eyebrow">
              YTD Budget <span className="fo-eyebrow-hint">· GL + project</span>
            </div>
          </div>
          <HeroValue raw={ytdBudget} />
          <div className="fo-bullet" title={`${utilisation.toFixed(1)}% utilised`}>
            <div className="fo-bullet-fill" style={{ width: Math.min(100, utilisation) + "%" }} />
          </div>
          <div className="fo-hero-sub">
            FY total <strong>{fmtCurrencyFull(fyBudget)}</strong>
          </div>
        </div>

        {/* Utilisation — accent */}
        <div className="fo-hero-tile fo-hero-utilisation">
          <div className="fo-eyebrow" style={{ alignSelf: "flex-start" }}>Budget Utilised (YTD)</div>
          <CircularProgress pct={utilisation} color={statusColor} />
          <div className="fo-status-pill" style={{ background: statusColor + "18", color: statusColor }}>
            <span className="fo-status-dot" style={{ background: statusColor }} />
            {status.label}
          </div>
          <div className="fo-util-context">
            <strong>{fmtCurrencyFull(Math.abs(ytdActuals))}</strong> of <strong>{fmtCurrencyFull(ytdBudget)}</strong>
          </div>
        </div>
      </div>

      {/* ── Secondary chip row ── */}
      <div className="fo-secondary">
        <div className="fo-secondary-chip">
          <span className="fo-chip-label">FY Actuals</span>
          <span className="fo-chip-value">{fmtCurrencyFull(Math.abs(fyActuals))}</span>
        </div>
        <div className="fo-secondary-chip">
          <span className="fo-chip-label">FY Budget</span>
          <span className="fo-chip-value">{fmtCurrencyFull(fyBudget)}</span>
        </div>
        <div className="fo-secondary-chip">
          <span className="fo-chip-label">YTD Revenue</span>
          <span className="fo-chip-value">{fmtCurrencyFull(Math.abs(ytdRevenue))}</span>
        </div>
      </div>

      {/* ── Primary chart: Monthly Burn ── */}
      {charts.monthlyExpVsBudget && charts.monthlyExpVsBudget.length > 0 && (
        <>
          <div className="fo-section-head">
            <span className="fo-section-title">Monthly Burn</span>
            <span className="fo-section-rule" />
            <span className="fo-section-hint">Actuals vs Budget · FY {fy || "—"}</span>
          </div>
          <div className="fo-chart-card">
            <div className="fo-chart-card-header">
              <div className="fo-chart-card-title">Actuals vs Budget by month</div>
              <div className="fo-chart-card-meta">
                <span className="fo-legend-swatch"><i style={{ background: tabColor }} />Actuals</span>
                <span className="fo-legend-swatch"><i style={{ background: "#9CA3AF" }} />Budget</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={charts.monthlyExpVsBudget} margin={{ top: 6, right: 18, left: 6, bottom: 6 }}>
                <defs>
                  <linearGradient id="fo-actuals-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={tabColor} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={tabColor} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER_SOFT} />
                <XAxis dataKey="Month" tick={{ fontSize: 11, fill: TEXT_2 }} tickFormatter={(v: string) => v?.slice(0, 3)} axisLine={{ stroke: BORDER }} tickLine={{ stroke: BORDER }} />
                <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: TEXT_3 }} axisLine={{ stroke: BORDER }} tickLine={{ stroke: BORDER }} width={56} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, padding: "8px 12px" }}
                  formatter={(v: number) => fmtCurrencyFull(v)}
                  labelStyle={{ color: TEXT_2, fontWeight: 600, marginBottom: 4 }}
                />
                <Area type="monotone" dataKey="Actuals" stroke="none" fill="url(#fo-actuals-fill)" />
                <Line type="monotone" dataKey="Budget" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                <Line type="monotone" dataKey="Actuals" stroke={tabColor} strokeWidth={2.5} dot={{ r: 3, fill: tabColor, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                {currentPeriodLabel && (
                  <ReferenceLine
                    x={currentPeriodLabel}
                    stroke={tabColor}
                    strokeDasharray="2 4"
                    label={{ value: "Selected", position: "top", fill: tabColor, fontSize: 10, fontWeight: 700 }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* ── Spend by Directorate (drillable) ── */}
      {charts.rollupSpend && charts.rollupSpend.length > 0 && (
        <>
          <div className="fo-section-head">
            <span className="fo-section-title">Spend Distribution</span>
            <span className="fo-section-rule" />
            <span className="fo-section-hint">Click a bar to drill in</span>
          </div>
          <div className="fo-chart-card">
            <div className="fo-chart-card-header">
              <div className="fo-chart-card-title">Spend by {charts.rollupLevel || "Directorate"}</div>
              <span className="fo-drill-hint">↘ Drillable</span>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(280, charts.rollupSpend.length * 32)}>
              <BarChart data={charts.rollupSpend} layout="vertical" margin={{ top: 6, right: 60, left: 6, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER_SOFT} horizontal={false} />
                <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: TEXT_3 }} axisLine={{ stroke: BORDER }} tickLine={{ stroke: BORDER }} />
                <YAxis type="category" dataKey="Name" width={220} tick={{ fontSize: 11.5, fill: TEXT_2, fontWeight: 600 }} tickFormatter={(v: string) => truncate(v, 30)} axisLine={{ stroke: BORDER }} tickLine={{ stroke: BORDER }} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, padding: "8px 12px" }}
                  formatter={(v: number) => fmtCurrencyFull(v)}
                  cursor={{ fill: tabColor + "10" }}
                />
                <Bar
                  dataKey="Spend"
                  radius={[0, 4, 4, 0]}
                  barSize={18}
                  onClick={onDrillBar as any}
                  cursor="pointer"
                >
                  <LabelList dataKey="Spend" content={rollupValueLabel as any} />
                  {charts.rollupSpend.map((_: any, i: number) => (
                    <Cell key={i} fill={tabPalette[i % tabPalette.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* ── Top expense accounts ── */}
      {charts.spendByAccount && charts.spendByAccount.length > 0 && (
        <>
          <div className="fo-section-head">
            <span className="fo-section-title">Where the Money Goes</span>
            <span className="fo-section-rule" />
            <span className="fo-section-hint">Top 10 expense accounts</span>
          </div>
          <div className="fo-chart-card">
            <div className="fo-chart-card-header">
              <div className="fo-chart-card-title">Expense by account</div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={charts.spendByAccount} layout="vertical" margin={{ top: 6, right: 60, left: 6, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER_SOFT} horizontal={false} />
                <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: TEXT_3 }} axisLine={{ stroke: BORDER }} tickLine={{ stroke: BORDER }} />
                <YAxis type="category" dataKey="Account" width={220} tick={{ fontSize: 11.5, fill: TEXT_2, fontWeight: 600 }} tickFormatter={(v: string) => truncate(v, 30)} axisLine={{ stroke: BORDER }} tickLine={{ stroke: BORDER }} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, padding: "8px 12px" }}
                  formatter={(v: number) => fmtCurrencyFull(v)}
                  cursor={{ fill: tabColor + "10" }}
                />
                <Bar dataKey="Spend" radius={[0, 4, 4, 0]} barSize={16}>
                  <LabelList dataKey="Spend" content={rollupValueLabel as any} />
                  {charts.spendByAccount.map((_: any, i: number) => (
                    <Cell key={i} fill={tabPalette[i % tabPalette.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </>
  );
}
